import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useSetRecoilState } from "recoil";
import { IoFlash } from "react-icons/io5";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { useUnlimitedOnlyPaywall } from "MVVM/Hooks/useUnlimitedOnlyPaywall";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { StripePaystationState, StorageAddonModalState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { TIER_STRIPE_LOOKUP } from "MVVM/Utilities/SubscriptionTiers";
import { EPriceTabView } from "MVVM/@types";
import type { ClipStatusTimeline } from "../../@types";
import { HAPPY_PATH_EVENTS } from "../../@types";
import { focusRing } from "MVVM/Components/Dock/styledComponents";

type SkipTheWaitCTAProps = {
  timeline: ClipStatusTimeline;
  activeIdx: number;
  isStale: boolean;
  isError: boolean;
};

// Upsell for FREE users stuck in the slow lane. Since pnd re-queues waiting
// clips into the faster capture queue on upgrade (pnd #291), upgrading also
// speeds up the clip they're watching, not just future ones. Only rendered
// during the wait states; hidden once recording starts.
export const SkipTheWaitCTA = ({
  timeline,
  activeIdx,
  isStale,
  isError,
}: SkipTheWaitCTAProps) => {
  const { allstarUser } = useUser();
  const { isTrialEligible, currentTier } = useTrialEligibility();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const setStripePaystation = useSetRecoilState(StripePaystationState);
  const setUpgradeModal = useSetRecoilState(StorageAddonModalState);
  const { unlimitedOnly } = useUnlimitedOnlyPaywall();
  const { resolve: resolveProvider } = useResolvedPaymentProvider();

  const isFree = allstarUser?.user?.userGroup?.type === "FREE";
  const activeEvent = activeIdx >= 0 ? HAPPY_PATH_EVENTS[activeIdx] : undefined;
  const isWaiting =
    !isError &&
    (isStale || activeEvent === "SUBMITTED" || activeEvent === "QUEUED");
  const visible = showSubscriptionOverhaul && isFree && isWaiting;

  // The honest wait the user was looking at when the CTA fired, for the
  // ALL-1820 readout. Raw minutes, not the display bucket.
  const etaIso = timeline.desktop?.estimatedCompletionAt;
  const etaMinutes = etaIso
    ? Math.max(0, Math.ceil((new Date(etaIso).getTime() - Date.now()) / 60000))
    : null;

  // Prop vocabulary matches "Upgrade Modal - *" (isTrial, currentTier) so the
  // trial funnel can be queried uniformly across surfaces; the dedicated event
  // name identifies this entry point. Never-subscribed users additionally
  // emit shared plan-picker exposure/checkout events after opening it.
  const trackingProps = {
    trackId: timeline.trackId,
    game: timeline.game,
    contentType: timeline.contentType,
    queueName: timeline.queueName,
    clipState: activeEvent,
    stale: isStale,
    etaMinutes,
    isTrial: isTrialEligible,
    currentTier,
  };

  const shownTrackIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!visible || shownTrackIds.current.has(timeline.trackId)) return;
    shownTrackIds.current.add(timeline.trackId);
    analytics.track("Clip Status Trial CTA Shown", {
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      queueName: timeline.queueName,
      clipState: activeEvent,
      stale: isStale,
      etaMinutes,
      isTrial: isTrialEligible,
      currentTier,
    });
  }, [
    visible,
    isTrialEligible,
    currentTier,
    activeEvent,
    isStale,
    etaMinutes,
    timeline,
  ]);

  if (!visible) return null;

  // Never-subscribed accounts use the shared offer picker, including its price
  // and plan-choice assignment. Former subscribers retain their existing flow.
  // Xsolla-routed (RU) users also go through the picker, which owns the
  // Stripe/Xsolla split; the direct Stripe paystation below is Stripe-only.
  const openCheckout = async () => {
    analytics.track("Clip Status Trial CTA Clicked", trackingProps);
    if (unlimitedOnly || (await resolveProvider()) === "xsolla") {
      setUpgradeModal({
        isOpen: true,
        analyticsSource: "Clip Status - Skip the Queue",
        initialPlan: "CREATOR_PLAT",
        initialBillingPeriod: EPriceTabView.Monthly,
      });
      return;
    }
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      lookUp: TIER_STRIPE_LOOKUP.CREATOR_PLAT[EPriceTabView.Monthly],
      trial: isTrialEligible,
    });
  };

  return (
    <TrialButton
      onClick={openCheckout}
      title={isTrialEligible ? "Free for 7 days · No card needed" : undefined}
      type="button"
    >
      <IoFlash size={14} />
      {isTrialEligible ? "Skip the queue for free" : "Skip the queue"}
    </TrialButton>
  );
};

// Shared with KeepTheSpeedCTA, the trial-user sibling in the same card slot.
// A quiet text action: the card's one loud moment belongs to the ready clip.
export const TrialButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  gap: 5px;
  line-height: 16px;
  margin-top: 8px;
  padding: 0;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  ${focusRing}
`;
